import { useDimensions } from "@repo/core/hooks/useDimensions";
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Text } from "@repo/core/components/core/Text";
import { convertDataRows } from "@repo/core/components/core/DataTable/utils";
import { TableConvertedData } from "@repo/core/components/core/DataTable/types";
import { exportTanstackTableToCSV } from "@repo/core/utils/csv.utils";
import { useMemo, useRef, useState } from "react";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import { ReportType, ReportTypeEnum } from "../../types";
import { SalesByStoreReportTypes } from "@repo/core/types/reports";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useDeviceType } from "@repo/core/hooks/useDeviceType";
import { ROUTES } from "@repo/core/routes";
import { ReportsResource } from "@repo/core/services/resources/reports";
import { newApInstance } from "@repo/core/services/instances/new-api";
import { useReportsFilters } from "@repo/core/hooks/useReportsFilters";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";

const reportsResource = new ReportsResource(newApInstance);

export const SalesByStoreReportController = () => {
  const { isMobile } = useDimensions();

  const { isMobile: isMobileDevice } = useDeviceType();
  const { push } = useNavigation();

  const {
    selectedAttendant,
    selectedProduct,
    selectedStore,
    filterStartDate,
    filterEndDate,
    onChangeStartDate,
    onChangeEndDate
  } = useReportsFilters();

  const [selectedReportType, setSelectedReportType] =
    useState<SalesByStoreReportTypes>(ReportTypeEnum.SALES);

  const filterModalRef = useRef<IModalRefProps>(null);

  const {
    data: salesByStoreReportResponse,
    isFetching: isFetchingSalesByStoreReport
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      "salesByStoreReport",
      filterStartDate,
      filterEndDate,
      selectedStore,
      selectedProduct,
      selectedAttendant,
      selectedReportType
    ],
    queryFn: () =>
      reportsResource.getGeneralReports({
        startDate: format(filterStartDate, "yyyy-MM-dd"),
        endDate: format(filterEndDate, "yyyy-MM-dd"),
        store: selectedStore,
        product: selectedProduct,
        attendant: selectedAttendant,
        type: selectedReportType
      })
  });

  const onSelectReportType = (reportType: ReportTypeEnum) => {
    setSelectedReportType(reportType as SalesByStoreReportTypes);
  };

  const columnHelper = createColumnHelper();

  const reportTypes: ReportType[] = [
    {
      title: "Vendas",
      type: ReportTypeEnum.SALES
    },
    {
      title: "Mais informações",
      type: ReportTypeEnum.MORE_INFO
    }
  ];

  const headerColumns = salesByStoreReportResponse?.data.table.headers.map(
    (header) => {
      return columnHelper.accessor(header.key, {
        header: () => (
          <Text
            fontSize="$xs"
            color={header?.styles?.text?.color || "$textLight700"}
            fontWeight={header?.styles?.text?.fontWeight || 600}
          >
            {header.label}
          </Text>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        meta: header?.styles
      });
    }
  );

  const { tableData, hasReportData } = useMemo(() => {
    const tableData = convertDataRows(
      salesByStoreReportResponse?.data?.table.headers,
      salesByStoreReportResponse?.data?.table.rows
    );

    const hasReportData =
      Array.isArray(salesByStoreReportResponse?.data?.table.rows) &&
      salesByStoreReportResponse.data.table.rows.length > 0;

    return {
      tableData,
      hasReportData
    };
  }, [salesByStoreReportResponse?.data]);

  const table = useReactTable({
    data: tableData,
    columns: headerColumns as AccessorKeyColumnDef<TableConvertedData>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel()
  });

  const handleExportTable = () => {
    exportTanstackTableToCSV(
      "Relatório_Vendas_Por_Loja",
      salesByStoreReportResponse?.data.table?.headers,
      table
    );
  };

  const handleSelectFilters = () => {
    if (isMobileDevice) {
      push(ROUTES.reportSelectFilters);
    } else {
      if (filterModalRef.current) {
        filterModalRef.current.open();
      }
    }
  };

  return {
    isMobile,
    onChangeStartDate,
    onChangeEndDate,
    table,
    handleExportTable,
    filterModalRef,
    reportTypes,
    selectedReportType,
    onSelectReportType,
    filterStartDate,
    filterEndDate,
    handleSelectFilters,
    hasReportData,
    isFetchingSalesByStoreReport
  };
};
